<template>
  <DefaultLayout>
    <div v-if="!bootstrapped" class="app-shell-boot">
      <n-spin size="large" />
    </div>
    <OrgNoPermissionBlock
      v-else-if="blockedPermissions"
      :permission="blockedPermissions"
    />
    <router-view v-else v-slot="{ Component }">
      <keep-alive>
        <component :is="Component" />
      </keep-alive>
    </router-view>
  </DefaultLayout>
  <ToastContainer />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import DefaultLayout from './default.vue'
import ToastContainer from '../components/ToastContainer.vue'
import OrgNoPermissionBlock from '../components/OrgNoPermissionBlock.vue'
import { useAuthStore } from '../stores/auth'
import { hasAnyOrgPermission, hasOrgPermission } from '../utils/orgPermissions'

const authStore = useAuthStore()
const route = useRoute()
const bootstrapped = ref(false)

/** 组织代登下各页所需权限（多权限时满足任一即可） */
const ROUTE_ORG_PERMS: Array<{ test: (path: string) => boolean; perms: string[] }> = [
  { test: (p) => p === '/modeling' || p.startsWith('/modeling/'), perms: ['campaign.view'] },
  {
    test: (p) => /^\/advertisers\/[^/]+\/campaigns/.test(p),
    perms: ['campaign.view'],
  },
  { test: (p) => p === '/products' || p.startsWith('/products/'), perms: ['product.view'] },
  {
    test: (p) => p === '/auto-placement' || p.startsWith('/auto-placement/'),
    perms: ['auto_placement.view'],
  },
  {
    test: (p) => p === '/budget-settings' || p.startsWith('/budget-settings/'),
    perms: ['budget.manage', 'roi.manage', 'algorithm.view'],
  },
  {
    test: (p) => p === '/algorithm-tasks' || p.startsWith('/algorithm-tasks/'),
    perms: ['algorithm.view'],
  },
  { test: (p) => p === '/materials' || p.startsWith('/materials/'), perms: ['material.view'] },
  {
    test: (p) => p === '/material-inspect' || p.startsWith('/material-inspect/'),
    perms: ['material.view'],
  },
  {
    test: (p) => p === '/rpa/tokens' || p.startsWith('/rpa/tokens/'),
    perms: ['rpa.view'],
  },
  {
    test: (p) => p === '/invalid-campaign-management' || p.startsWith('/invalid-campaign-management/'),
    perms: ['invalid_campaign.view'],
  },
  {
    test: (p) => p === '/package-center' || p.startsWith('/package-center/'),
    perms: ['package.view'],
  },
  {
    test: (p) => p === '/messages' || p.startsWith('/messages/'),
    perms: ['message.view'],
  },
  {
    test: (p) => p === '/roi-adjustments' || p.startsWith('/roi-adjustments/'),
    perms: ['campaign.view'],
  },
  {
    test: (p) => p === '/advertisers' || p === '/advertisers/',
    perms: ['shop.view', 'campaign.view', 'shop.config.view'],
  },
]

/** 代登下明确不开放的页面（即使有相关权限也不进） */
const ORG_DENY_PATHS: Array<(path: string) => boolean> = [
  (p) => p === '/campaigns' || p.startsWith('/campaigns/'),
  (p) => p === '/org-operation-logs' || p.startsWith('/org-operation-logs/'),
]

const blockedPermissions = computed<string[] | null>(() => {
  if (!authStore.orgImpersonating) return null
  const path = route.path
  if (ORG_DENY_PATHS.some((t) => t(path))) return ['__org_page_denied__']
  const rule = ROUTE_ORG_PERMS.find((r) => r.test(path))
  if (!rule) return null
  const granted = authStore.orgImpersonation?.permissions || []
  const ok =
    rule.perms.length === 1
      ? hasOrgPermission(granted, rule.perms[0])
      : hasAnyOrgPermission(granted, rule.perms)
  return ok ? null : rule.perms
})

onMounted(async () => {
  // 始终刷新：组织代登 cookie 可能在硬跳转后才写入，路由守卫首次缓存用户时也需同步 org 字段
  await authStore.fetchUserInfo()
  if (!authStore.hasQianchuanAuth) {
    await authStore.fetchAuthStatus()
  }
  bootstrapped.value = true
})

// 代登中切页时再拉一次，确保客户改范围后前端权限及时对齐
watch(
  () => route.fullPath,
  async () => {
    if (!bootstrapped.value || !authStore.orgImpersonating) return
    await authStore.fetchUserInfo()
  }
)
</script>

<style scoped>
.app-shell-boot {
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
